import React from 'react';
import { calculatePortfolioCashflows, calculateIRR, formatCurrency, formatPercentage } from '../utils/calculations';

const PortfolioSummary = ({ portfolio, investmentAmount }) => {
  // Add null checks for portfolio
  if (!portfolio) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <h3 className="text-lg font-semibold text-dark-blue mb-2">Resumen</h3>
        <div className="text-gray-500 text-center py-4">
          Invalid portfolio data
        </div>
      </div>
    );
  }

  if (!investmentAmount || investmentAmount <= 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <h3 className="text-lg font-semibold text-dark-blue mb-2">Resumen - {portfolio.name || 'Portfolio'}</h3>
        <div className="text-gray-500 text-center py-4">
          Ingresa una inversión para ver los cálculos
        </div>
      </div>
    );
  }

  try {
    // Validate portfolio data before calculations
    if (!portfolio.projects || !Array.isArray(portfolio.projects)) {
      throw new Error('Invalid portfolio projects data');
    }

    // Calculate the maximum month needed based on exitMonth
    const maxMonth = Math.max(...portfolio.projects.map(project => project.exitMonth || 42));
    
    // Initialize cashflows array with the correct length
    const cashflows = new Array(maxMonth).fill(0);
    
    // Calculate monthly cashflows
    portfolio.projects.forEach(project => {
      if (project && typeof project.capitalDistribution === 'number') {
        const projectInvestment = investmentAmount * project.capitalDistribution;
        
        // Add initial investment (negative cashflow)
        if (project.startMonth > 0 && project.startMonth <= maxMonth) {
          cashflows[project.startMonth - 1] -= projectInvestment;
        }
        
        // Add monthly interest until exit month
        for (let month = project.startMonth; month < project.exitMonth && month <= maxMonth; month++) {
          cashflows[month - 1] += (projectInvestment * project.interestRate / 12);
        }
        
        // Add capital return at exit month
        if (project.exitMonth > 0 && project.exitMonth <= maxMonth) {
          const exitMultiple = portfolio.id === 2 ? (project.exitMultiple || 1.0) : 1.0;
          cashflows[project.exitMonth - 1] += projectInvestment * exitMultiple;
        }
      }
    });

    const monthlyIrr = calculateIRR(cashflows);
    // Convert monthly IRR to annual IRR
    const annualIrr = (Math.pow(1 + monthlyIrr, 12) - 1);
    
    const totalInvestment = investmentAmount;
    
    // Calculate total interest (monthly interest payments only)
    let totalInterest = 0;
    portfolio.projects.forEach(project => {
      if (project && typeof project.capitalDistribution === 'number' && typeof project.interestRate === 'number') {
        const projectInvestment = investmentAmount * project.capitalDistribution;
        const monthlyInterest = projectInvestment * project.interestRate / 12;
        const numInterestPayments = project.exitMonth - project.startMonth;
        totalInterest += monthlyInterest * numInterestPayments;
      }
    });
    
    // Calculate total capital returns (with exit multiple for Venture Debt)
    let totalCapitalReturn = 0;
    portfolio.projects.forEach(project => {
      if (project && typeof project.capitalDistribution === 'number') {
        const projectInvestment = investmentAmount * project.capitalDistribution;
        const exitMultiple = portfolio.id === 2 ? (project.exitMultiple || 1.0) : 1.0;
        totalCapitalReturn += projectInvestment * exitMultiple;
      }
    });

    // Calculate actual exit multiple based on total capital return vs investment
    const actualExitMultiple = portfolio.id === 2 
      ? totalCapitalReturn / totalInvestment
      : 1.0;

    return (
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <h3 className="text-lg font-semibold text-dark-blue mb-4">Resumen - {portfolio.name || 'Portfolio'}</h3>
        
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-red-200 whitespace-nowrap min-w-[180px] flex-1">
            <div className="text-sm text-gray-600 font-medium mb-1">Inversión</div>
            <div className="text-xl font-semibold text-red-700">{formatCurrency(totalInvestment)}</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-emerald-200 whitespace-nowrap min-w-[180px] flex-1">
            <div className="text-sm text-gray-600 font-medium mb-1">Interes Total</div>
            <div className="text-xl font-semibold text-emerald-700">{formatCurrency(totalInterest)}</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-blue-200 whitespace-nowrap min-w-[180px] flex-1">
            <div className="text-sm text-gray-600 font-medium mb-1">Regreso de Capital</div>
            <div className="text-xl font-semibold text-blue-700">{formatCurrency(totalCapitalReturn)}</div>
          </div>
          {portfolio.id === 2 && (
            <div className="bg-white p-4 rounded-lg border border-purple-200 whitespace-nowrap min-w-[180px] flex-1">
              <div className="text-sm text-gray-600 font-medium mb-1">Multiple de Salida</div>
              <div className="text-xl font-semibold text-purple-700">{actualExitMultiple.toFixed(2)}x</div>
            </div>
          )}
          <div className="bg-white p-4 rounded-lg border border-amber-200 whitespace-nowrap min-w-[180px] flex-1">
            <div className="text-sm text-gray-600 font-medium mb-1">TIR Anual</div>
            <div className="text-xl font-semibold text-amber-700">{formatPercentage(annualIrr)}</div>
          </div>
        </div>

        <div>
          <h4 className="text-md font-semibold text-dark-blue mb-3">Flujo Mensual</h4>
          <div className="custom-scrollbar relative bg-white rounded-lg border border-gray-200" style={{width: '100%'}}>
            <div style={{minWidth: 'max-content', paddingBottom: '4px'}}>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-gray-50 py-4 px-4 text-left font-semibold text-gray-600 border-b border-gray-200">Concepto</th>
                    {Array.from({ length: maxMonth }, (_, i) => (
                      <th key={i} className="bg-gray-50 py-4 px-4 text-right font-semibold text-gray-600 border-b border-gray-200">M{i + 1}</th>
                    ))}
                    <th className="bg-gray-50 py-4 px-4 text-right font-semibold text-gray-600 border-b border-gray-200">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sticky left-0 bg-white py-3 px-4 text-left font-medium text-red-700 border-b border-gray-100">Inversión</td>
                    {Array.from({ length: maxMonth }, (_, month) => {
                      let investment = 0;
                      portfolio.projects.forEach(project => {
                        if (project && typeof project.capitalDistribution === 'number') {
                          if (month + 1 === project.startMonth) {
                            investment -= investmentAmount * project.capitalDistribution;
                          }
                        }
                      });
                      return (
                        <td key={month} className="py-3 px-4 text-right border-b border-gray-100">
                          <span className={investment !== 0 ? 'text-red-600' : 'text-gray-400'}>
                            {investment !== 0 ? formatCurrency(investment) : '-'}
                          </span>
                        </td>
                      );
                    })}
                    <td className="py-3 px-4 text-right font-medium text-red-700 border-b border-gray-100 bg-gray-50">{formatCurrency(-totalInvestment)}</td>
                  </tr>
                  <tr>
                    <td className="sticky left-0 bg-white py-3 px-4 text-left font-medium text-emerald-700 border-b border-gray-100">Interés</td>
                    {Array.from({ length: maxMonth }, (_, month) => {
                      let interest = 0;
                      portfolio.projects.forEach(project => {
                        if (project && typeof project.capitalDistribution === 'number') {
                          if (month + 1 >= project.startMonth && month + 1 < project.exitMonth) {
                            interest += (investmentAmount * project.capitalDistribution * project.interestRate / 12);
                          }
                        }
                      });
                      return (
                        <td key={month} className="py-3 px-4 text-right border-b border-gray-100">
                          <span className={interest !== 0 ? 'text-emerald-600' : 'text-gray-400'}>
                            {interest !== 0 ? formatCurrency(interest) : '-'}
                          </span>
                        </td>
                      );
                    })}
                    <td className="py-3 px-4 text-right font-medium text-emerald-700 border-b border-gray-100 bg-gray-50">{formatCurrency(totalInterest)}</td>
                  </tr>
                  <tr>
                    <td className="sticky left-0 bg-white py-3 px-4 text-left font-medium text-blue-700 border-b border-gray-100">Retorno Capital</td>
                    {Array.from({ length: maxMonth }, (_, month) => {
                      let capitalReturn = 0;
                      portfolio.projects.forEach(project => {
                        if (project && typeof project.capitalDistribution === 'number') {
                          if (month + 1 === project.exitMonth) {
                            const exitMultiple = portfolio.id === 2 ? (project.exitMultiple || 1.0) : 1.0;
                            capitalReturn += investmentAmount * project.capitalDistribution * exitMultiple;
                          }
                        }
                      });
                      return (
                        <td key={month} className="py-3 px-4 text-right border-b border-gray-100">
                          <span className={capitalReturn !== 0 ? 'text-blue-600' : 'text-gray-400'}>
                            {capitalReturn !== 0 ? formatCurrency(capitalReturn) : '-'}
                          </span>
                        </td>
                      );
                    })}
                    <td className="py-3 px-4 text-right font-medium text-blue-700 border-b border-gray-100 bg-gray-50">{formatCurrency(totalCapitalReturn)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error in PortfolioSummary:', error);
    return (
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <h3 className="text-lg font-semibold text-dark-blue mb-2">Error</h3>
        <div className="text-red-500 text-center py-4">
          An error occurred while calculating portfolio summary
        </div>
      </div>
    );
  }
};

export default PortfolioSummary; 